import { ReactNode, createContext, useContext } from 'react';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { Notification } from '@/shared/types/api.types';
import { nanoid } from 'nanoid';

interface NotificationState {
  notifications: Notification[];
}

interface NotificationActions {
  addNotification: (notification: Omit<Notification, 'id' | 'createdAt'>) => void;
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;
  markAsRead: (id: string) => void;
}

type NotificationStore = NotificationState & NotificationActions;

const useNotificationStore = create<NotificationStore>()(
  immer((set) => ({
    notifications: [],

    addNotification: (notification) =>
      set((state) => {
        const newNotification: Notification = {
          ...notification,
          id: nanoid(),
          createdAt: Date.now(),
        };
        state.notifications.unshift(newNotification);
        
        // Auto-remove after duration
        if (notification.duration) {
          setTimeout(() => {
            set((state) => {
              const index = state.notifications.findIndex(n => n.id === newNotification.id);
              if (index > -1) {
                state.notifications.splice(index, 1);
              }
            });
          }, notification.duration);
        }
      }),

    removeNotification: (id) =>
      set((state) => {
        const index = state.notifications.findIndex(n => n.id === id);
        if (index > -1) {
          state.notifications.splice(index, 1);
        }
      }),

    clearAllNotifications: () =>
      set((state) => {
        state.notifications = [];
      }),

    markAsRead: (id) =>
      set((state) => {
        const notification = state.notifications.find(n => n.id === id);
        if (notification) {
          // For now, just remove read notifications
          const index = state.notifications.findIndex(n => n.id === id);
          if (index > -1) {
            state.notifications.splice(index, 1);
          }
        }
      }),
  }))
);

const NotificationContext = createContext<NotificationStore | null>(null);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider = ({ children }: NotificationProviderProps) => {
  const store = useNotificationStore();

  return (
    <NotificationContext.Provider value={store}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};